import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fireLead } from "@/lib/tracking";

interface BookingLeadFormProps {
  className?: string;
  treatmentInterest?: string;
}

const BookingLeadForm = ({ className, treatmentInterest }: BookingLeadFormProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (formData: FormData) => {
    const newErrors: Record<string, string> = {};
    
    // Email Validation
    const email = formData.get("email") as string;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Australian Mobile Validation
    // Matches: 04xx xxx xxx, +61 4xx xxx xxx, etc. (spaces optional)
    const phone = formData.get("phone") as string;
    const phoneClean = phone.replace(/[\s()-]/g, "");
    // Check for Aus mobile format: starts with 04 (10 digits) or 614 (11 digits)
    const ausMobileRegex = /^(04\d{8}|614\d{8})$/;
    
    if (!phone || !ausMobileRegex.test(phoneClean)) {
      newErrors.phone = "Please enter a valid Australian mobile number (e.g., 0400 000 000).";
    }

    if (!formData.get("firstName")) newErrors.firstName = "First name is required.";
    if (!formData.get("lastName")) newErrors.lastName = "Last name is required.";

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    
    const formData = new FormData(e.currentTarget);
    const formErrors = validate(formData);

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setLoading(true);

    try {
        const payload = {
            firstName: formData.get("firstName"),
            lastName: formData.get("lastName"),
            email: formData.get("email"),
            phone: formData.get("phone"),
            patientType: formData.get("patientType"),
            treatmentInterest: formData.get("treatmentInterest") || undefined,
            message: formData.get("message"),
        };

        const response = await fetch("/api/send-booking-request", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || `Failed with status ${response.status}`);
        }

        // ── Conversion tracking ─────────────────────────────────────────
        // fireLead() does three things:
        //   1. Pushes booking_form_submit to GTM dataLayer (so all GTM
        //      conversion tags fire — Google Ads + GA4)
        //   2. Hashes email + phone via SHA-256 and sets gtag user_data so
        //      Google Ads' Enhanced Conversions can match offline conversions
        //      back to the click that drove them (huge attribution boost)
        //   3. Fires a direct gtag conversion send_to AW-.../<label> if the
        //        conversion label is configured in src/data/tracking.json
        // The shared event_id returned from fireLead() can be used later to
        // dedupe with server-side / offline imports.
        try {
            await fireLead({
                service: (formData.get("treatmentInterest") as string) || (formData.get("patientType") as string) || 'general',
                email: payload.email as string,
                phone: payload.phone as string,
                page: window.location.pathname,
            });
        } catch (trackingErr) {
            // Never let tracking failures break the user-facing success state
            console.warn('Lead tracking failed (non-blocking):', trackingErr);
        }

        // Additional dataLayer push with form-specific metadata for GTM
        // dataLayer variables / segmentation. fireLead already pushed the
        // canonical booking_form_submit event for conversion triggers.
        if (typeof window !== 'undefined' && (window as any).dataLayer) {
            (window as any).dataLayer.push({
                event: 'booking_form_submitted_meta',
                form_type: 'booking_lead_form',
                form_language: 'English',
                patient_type: formData.get("patientType"),
                page_path: window.location.pathname,
                page_url: window.location.href,
            });
        }

        setSuccess(true);
        toast({
            title: "Request Sent!",
            description: "Check your email for the booking link.",
        });

    } catch (error) {
        console.error('Booking submission error:', error);
        toast({
            title: "Error",
            description: "Something went wrong. Please try again or call 03 9022 4442.",
            variant: "destructive",
        });
    } finally {
        setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`h-full flex flex-col items-center justify-center text-center p-8 space-y-6 ${className}`}>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <div>
            <h3 className="text-2xl font-bold text-neutral-800 mb-2">Details Sent!</h3>
            <p className="text-neutral-600 mb-6 max-w-sm mx-auto">
                We've sent a secure booking link to your email. Click it to choose your preferred time slot.
            </p>
            <Button asChild variant="outline" onClick={() => setSuccess(false)}>
                <span>Send another request</span>
            </Button>
        </div>
      </div>
    );
  }

    return (
        <div id="booking-form" className={`flex flex-col h-full ${className}`}>
            <form onSubmit={handleSubmit} className="flex-1 space-y-4">
                {treatmentInterest && (
                    <input type="hidden" name="treatmentInterest" value={treatmentInterest} />
                )}
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label htmlFor="firstName">First name <span className="text-red-500">*</span></Label>
                        <Input id="firstName" name="firstName" placeholder="John" required />
                        {errors.firstName && <p className="text-xs text-red-500">{errors.firstName}</p>}
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="lastName">Last name <span className="text-red-500">*</span></Label>
                        <Input id="lastName" name="lastName" placeholder="Doe" required />
                        {errors.lastName && <p className="text-xs text-red-500">{errors.lastName}</p>}
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="email">Email address <span className="text-red-500">*</span></Label>
                    <Input id="email" name="email" type="email" placeholder="john@example.com" required />
                    {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="phone">Mobile number <span className="text-red-500">*</span></Label>
                    <Input id="phone" name="phone" type="tel" placeholder="0400 000 000" required />
                    {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="patientType">Are you a new patient? <span className="text-red-500">*</span></Label>
                    <select
                        id="patientType"
                        name="patientType"
                        defaultValue="New patient"
                        required
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    >
                        <option value="New patient">New patient</option>
                        <option value="Existing patient">Existing patient</option>
                    </select>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="message">Message (Optional)</Label>
                    <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell us about your dental needs..."
                        className="min-h-[80px]"
                    />
                </div>

                <div className="pt-2">
                    <Button type="submit" className="w-full text-lg font-bold py-6" disabled={loading}>
                        {loading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            "Submit booking"
                        )}
                    </Button>
                    <p className="text-xs text-neutral-500 text-center mt-3">
                        We'll email you a link to finalize your booking.
                    </p>
                </div>
            </form>
        </div>
  );
};

export default BookingLeadForm;
